/**
 * SOLOMON — CALL QUEUE PANEL
 * Tier 1 cold-call workflow surface (Indiana 2026-05-11).
 *
 * Loads /api/calls/queue and presents the prioritized list of contactable
 * IN leads to call today. Click a lead → opens the existing Leads view
 * via global event so the operator can read context + log the disposition.
 *
 * No telephony — operator dials the phone number from their cell. This
 * panel is the read/curation surface; disposition logging lives on
 * LeadDetail.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DialerPanel from '../Leads/DialerPanel';
import './callQueuePanel.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function formatCurrency(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (!isFinite(num)) return '—';
  return `$${Math.round(num).toLocaleString()}`;
}

function formatRelative(ts) {
  if (!ts) return 'never';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)    return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function CallQueuePanel() {
  const { authFetch } = useAuth();

  const [queue, setQueue]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [stateFilter, setStateFilter] = useState('IN');
  const [dialing, setDialing] = useState(false); // Dialer Phase 1: power-dial-assist loop

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${API_BASE}/api/calls/queue?state=${stateFilter}&limit=50`);
      const json = await r.json();
      if (!r.ok || !json.success) throw new Error(json.error || 'Failed to load queue');
      setQueue(json.data.queue);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, stateFilter]);

  useEffect(() => { load(); }, [load]);

  const openLead = (leadId) => {
    // Match existing ToolModal navigation pattern — dispatch a global event
    // that App.jsx listens for to swap activeTool='leads' and select the lead.
    window.dispatchEvent(new CustomEvent('solomon:open-lead', { detail: { leadId } }));
  };

  // Dialer Phase 1: the power-dial-assist loop takes over the panel when active.
  if (dialing) {
    return <DialerPanel onExit={() => { setDialing(false); load(); }} />;
  }

  return (
    <div className="call-queue-panel">
      <div className="cq-header">
        <div className="cq-title-block">
          <h2 className="cq-title">Today's Call Queue</h2>
          <p className="cq-subtitle">
            Contactable leads in {stateFilter}, ranked by motivation — most distressed first.
            TCPA gate pre-applied — re-checked on dial.
          </p>
        </div>
        <div className="cq-controls">
          <label className="cq-state-select">
            State
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value.toUpperCase())}>
              <option value="IN">IN — Indiana</option>
              <option value="AL">AL — Alabama (historical)</option>
            </select>
          </label>
          <button className="cq-refresh-btn" onClick={() => setDialing(true)}>
            Start calling
          </button>
          <button className="cq-refresh-btn" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="cq-error">Error: {error}</div>
      )}

      {!error && !loading && queue.length === 0 && (
        <div className="cq-empty">
          <div className="cq-empty-title">Queue is empty for {stateFilter}.</div>
          <div className="cq-empty-hint">
            Either no contactable leads exist in this state, or every lead was
            called within the last 24 hours. Use "Find Leads" to pull a fresh
            BatchData batch, or wait the cooldown.
          </div>
        </div>
      )}

      {!error && queue.length > 0 && (
        <div className="cq-stats">
          {queue.length} lead{queue.length === 1 ? '' : 's'} ready
        </div>
      )}

      <div className="cq-list">
        {queue.map((lead) => (
          <button
            key={lead.id}
            className="cq-row"
            onClick={() => openLead(lead.id)}
            title="Open lead detail to log call disposition"
          >
            <div className="cq-row-left">
              <div className="cq-row-name">
                {lead.owner_full_name || `${lead.owner_first_name || ''} ${lead.owner_last_name || ''}`.trim() || '— no name —'}
              </div>
              <div className="cq-row-address">
                {lead.property_address}
                {lead.property_city ? `, ${lead.property_city}` : ''}
                {lead.property_state ? `, ${lead.property_state}` : ''}
                {lead.property_zip ? ` ${lead.property_zip}` : ''}
              </div>
              <div className="cq-row-meta">
                {lead.phone_primary && <span className="cq-row-phone">{lead.phone_primary}</span>}
              </div>
              {lead.chips?.length > 0 && (
                <div className="cq-row-chips">
                  {lead.chips.map((chip) => (
                    <span
                      key={chip.key}
                      className={`cq-chip cq-chip--${chip.severity || 'medium'}`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="cq-row-right">
              <div className="cq-row-equity">
                <div className="cq-row-equity-label">Equity</div>
                <div className="cq-row-equity-val">{formatCurrency(lead.equity_estimate)}</div>
              </div>
              <div className="cq-row-touch">
                <div className="cq-row-touch-label">Last touch</div>
                <div className="cq-row-touch-val">
                  {lead.attempt_count > 0
                    ? `${formatRelative(lead.last_attempt_at)} (${lead.attempt_count})`
                    : 'never'}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
